import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as fromRoot from '../../../../../../../../../state/app.state';
import { projectDetails } from '../../../../../../../core/projects.selectors';
import { ProjectsService } from '../../../../../../../core/projects.service';
import { MaterialService } from '../../../../../../../../../shared/services/material.service';
import { Project } from '../../../../../../../../../shared/models';
import { Material } from '../../../../../../../../../shared/models/material';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../shared/components';
import { DeliveryTrackingMaterialsEditComponent } from '../delivery-tracking-materials-edit/delivery-tracking-materials-edit.component';

@Component({
    selector: 'app-delivery-tracking-materials',
    templateUrl: './delivery-tracking-materials.component.html',
    styleUrls: ['./delivery-tracking-materials.component.scss'],
    standalone: false
})
export class DeliveryTrackingMaterialsComponent implements OnInit, OnDestroy {
  project: Project;
  displayedColumns: string[] = ['name', 'uom', 'trackReceived', 'trackUsed', 'bidItems', 'actions'];
  dataSource: Material[] = [];
  filteredDataSource: Material[] = [];
  originalDataSource: Material[] = [];
  selectedRow: Material | null = null;
  totalRecords = 0;
  pagination = {
    pageIndex: 0,
    pageSize: 10,
  };
  currentPage = 1;
  itemsPerPage = 10;

  private readonly onDestroy = new Subject<void>();

  constructor(
    private store: Store<fromRoot.State>,
    private projectsService: ProjectsService,
    private materialService: MaterialService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        if (project && Object.keys(project).length) {
          this.project = project;
          this.fetchMaterials();
        }
      });

    this.projectsService.closeRightPanel
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.selectedRow = null;
      });
  }

  fetchMaterials(): void {
    if (!this.project?.id) { return; }

    this.materialService.getRecords(this.project, { pageIndex: 0, pageSize: 10000 })
      .then((resp: any) => {
        const materials = resp.result || [];
        this.originalDataSource = materials;
        this.filteredDataSource = [...this.originalDataSource];
        this.totalRecords = resp.meta?.totalRecords ?? this.originalDataSource.length;
        this.paginateData(true);
      })
      .catch(() => {
        this.snackBar.open('Failed to load materials', '', { duration: 3000 });
      });
  }

  rowClicked(row: Material): void {
    this.selectedRow = row;

    this.projectsService.openRightPanel.next({
      component: DeliveryTrackingMaterialsEditComponent,
      data: {
        material: row,
        projectId: this.project?.id,
        onSave: () => this.fetchMaterials(),
      },
    });
  }

  create(): void {
    this.projectsService.openRightPanel.next({
      component: DeliveryTrackingMaterialsEditComponent,
      data: {
        projectId: this.project?.id,
        onSave: () => this.fetchMaterials(),
      },
    });
  }

  editMaterial(row: Material, event: Event): void {
    event.stopPropagation();
    this.rowClicked(row);
  }

  deleteMaterial(row: Material, event: Event): void {
    event.stopPropagation();
    const name = row?.name || 'this material';
    const message = `Deleting <b>${name}</b> cannot be undone.<br />Proceed to delete?`;
    const dialogData = new ConfirmDialogModel('Delete?', message);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: dialogData,
    });

    dialogRef.afterClosed().pipe(takeUntil(this.onDestroy)).subscribe(result => {
      if (result) {
        this.materialService.delete(row)
          .then(() => {
            this.snackBar.open('Material deleted', '', { duration: 3000 });
            this.fetchMaterials();
          })
          .catch(() => {
            this.snackBar.open('Failed to delete material', '', { duration: 3000 });
          });
      }
    });
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.pagination.pageIndex = page - 1;
    this.paginateData();
  }

  paginateData(reset = false): void {
    if (reset) {
      this.currentPage = 1;
    }
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, this.filteredDataSource.length);

    this.dataSource = this.filteredDataSource.slice(startIndex, endIndex);
  }

  getBidItemNames(material: Material): string {
    if (!material.bid_items?.length) { return '—'; }
    return material.bid_items.map(bi => bi.item || bi.description).join(', ');
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
